import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Table, Typography, Card as CardAnt } from 'antd';

import {
  ObjectFormSection
} from '../../../../../../../../../shared';
import {
  ColSize,
  ContentSection,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../../../shared/components';

import { StepFourFormContext } from '../../../../../../contexts';
import { StepTwoFormContext } from '../../../../../../../StepTwo/contexts';
import { GenericDataFormContext, QuotationInfoContext } from '../../../../../../../../../shared/contexts';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../../../../../../../shared/utils';
import { ComponentTheftInsuranceQuotation } from '../../../../../../../../../../../../../shared/interfaces/services/theft-insurance-quotation-service';
import { COLORS } from '../../../../../../../../../../../../../config/themes/colors';
import { TransferRightsForm } from '../../../TransferRightsForm';

import './index.css';

interface Coverage {
  codigo: string;
  description: string;
  familyCode?: string;
  familyDescription?: string;
  key: string;
  riskCode: string;
  sumInsured: string;
  typeCoverage?: string;
}

interface CoverageGroup {
  [familyCode: string]: Coverage[];
}

interface CoveragesProps {
  theftInsuranceComponent: ComponentTheftInsuranceQuotation;
}

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Descripción de cobertura',
    dataIndex: 'description',
    width: '50%',
  },
  {
    title: 'Suma asegurada',
    dataIndex: 'sumInsured',
    width: '50%',
  },
];

export const FormCoverages: FC<CoveragesProps> = ({
  theftInsuranceComponent,
}) => {
  const validateForm = new ValidateForm();

  const { propertyDataForm } = useContext(StepFourFormContext);
  const { stepTwoForm } = useContext(StepTwoFormContext);
  const { provinceList, allLocalitiesPerProvince, allCoveragesPerPlan } =
    useContext(GenericDataFormContext);

  const [coveragesListInfo, setCoveragesListInfo] = useState<CoverageGroup[]>(
    []
  );

  const provinceValueByTab = stepTwoForm?.getFieldValue(
    `province-tab-${theftInsuranceComponent.nroDeComponente}`
  );
  const locationValueByTab = stepTwoForm?.getFieldValue(
    `location-tab-${theftInsuranceComponent.nroDeComponente}`
  );
  const localitiesPerProvince = allLocalitiesPerProvince.find(
    (loc) => String(loc.provinceCode) === String(provinceValueByTab)
  )?.localitiesOptions;

  const selectedPlanCode = stepTwoForm?.getFieldValue(
    `plan-tab-${theftInsuranceComponent.nroDeComponente}`
  );

  const setValuesForm = () => {
    propertyDataForm.setFieldValue(
      `propertyDataProvince${theftInsuranceComponent.nroDeComponente}`,
      provinceValueByTab
    );
    propertyDataForm.setFieldValue(
      `propertyDataLocality${theftInsuranceComponent.nroDeComponente}`,
      locationValueByTab
    );
  };

  const propertyDataAddressValueByTab = stepTwoForm?.getFieldValue(`propertyDataAddress${theftInsuranceComponent.nroDeComponente}`);

  useEffect(() => {
    setValuesForm();
  }, [theftInsuranceComponent]);

  const setCompleteCoveragesListInfo = useCallback(() => {
    const matchingCoveragesByTab = allCoveragesPerPlan.find(
      (el) => String(el.plan as string) === String(selectedPlanCode)
    );

    if (matchingCoveragesByTab) {
      const filteredCoverages = matchingCoveragesByTab.coverages.filter(
        (planCoverage) =>
          theftInsuranceComponent.coberturas.some(
            (cov) => cov.codigoCobertura === planCoverage.codigo
          )
      );

      const coveragesListInfo = filteredCoverages.map((planCoverage) => {
        const matchedCoverage = theftInsuranceComponent.coberturas.find(
          (cov) => cov.codigoCobertura === planCoverage.codigo
        );

        let familyDescription;

        if (planCoverage?.codigoFamilia) {
          familyDescription = planCoverage.descripcionFamilia;
        } else if (planCoverage.tipoDeCobertura === 'O') {
          familyDescription = 'ADICIONAL';
        } else {
          familyDescription = 'OTRAS';
        }

        return {
          codigo: planCoverage.codigo,
          description: planCoverage.descripcionLarga,
          familyCode:
            planCoverage?.codigoFamilia ??
            (planCoverage.tipoDeCobertura === 'O'
              ? 'additional'
              : 'noFamilyCode'),
          familyDescription,
          key: planCoverage.codigo,
          riskCode: planCoverage.codigoRiesgo,
          sumInsured: `$ ${formattedPrice(
            `${matchedCoverage ? matchedCoverage.sumaAsegurada : 0}`
          )}`,
          typeCoverage: planCoverage?.tipoDeCobertura,
        };
      });

      const groupedData = coveragesListInfo.reduce<{
        [key: string]: Coverage[];
      }>((acc, item) => {
        let codeFamilyData: string;

        codeFamilyData = item.familyCode;

        if (!acc[codeFamilyData]) acc[codeFamilyData] = [];

        acc[codeFamilyData].push(item);
        return acc;
      }, {});

      const desiredOrder = ['noFamilyCode', 'additional'];
      const orderedGroupedData: { [key: string]: Coverage[] } = {};

      desiredOrder.forEach((key) => {
        if (groupedData[key]) orderedGroupedData[key] = groupedData[key];
      });

      Object.keys(groupedData)
        .filter((key) => !desiredOrder.includes(key))
        .forEach((key) => {
          orderedGroupedData[key] = groupedData[key];
        });

      setCoveragesListInfo([orderedGroupedData]);

      return;
    }

    setCoveragesListInfo([]);
  }, [allCoveragesPerPlan]);

  useEffect(() => {
    setCompleteCoveragesListInfo();
  }, [setCompleteCoveragesListInfo]);

  const { renewalQuotationState } = useContext(QuotationInfoContext);
  const isRenewal = !!renewalQuotationState;

  return (
    <CardAnt>
      <ContentSection level='h3' title='Datos del bien'>
        <Form
          formProps={{
            form: propertyDataForm,
            name: `activity-form-${theftInsuranceComponent.nroDeComponente}`,
          }}
        >
          <RowSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Domicilio:',
                  name: `propertyDataAddress${theftInsuranceComponent.nroDeComponente}`,
                  rules: validateForm.generateRules({
                    label: 'domicilio',
                    rules: ['required'],
                  }),
                  initialValue: isRenewal ? (propertyDataAddressValueByTab ?? '') : '',
                }}
                inputProps={{ placeholder: 'Senillosa 342 1B', disabled: !!propertyDataAddressValueByTab && isRenewal, }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Provincia:',
                  name: `propertyDataProvince${theftInsuranceComponent.nroDeComponente}`,
                }}
                selectProps={{
                  disabled: true,
                  loading: provinceList?.length === 0,
                  options: provinceList,
                  placeholder: 'Seleccionar',
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Localidad:',
                  name: `propertyDataLocality${theftInsuranceComponent.nroDeComponente}`,
                }}
                selectProps={{
                  disabled: true,
                  options: localitiesPerProvince,
                  placeholder: 'Seleccionar',
                }}
              />
            </ColSize>
          </RowSize>
        </Form>
      </ContentSection>

      <ContentSection level='h3' title='Cobertura' hasCard={false}>
        {coveragesListInfo?.map((coverageGroup: any) => {
          const familyCodes = Object.keys(coverageGroup);

          return familyCodes.map((familyCode, index) => {
            const coverages = coverageGroup[familyCode];

            return (
              <React.Fragment key={`${familyCode}-${index}`}>
                <ColSize size={100} className={`${index > 0 ? 'mt-2' : ''}`}>
                  <Typography.Title
                    level={5}
                    style={{
                      margin: 0,
                      marginBottom: 10,
                      color: COLORS.secondary[100],
                    }}
                  >
                    {coverages[0]?.familyDescription}
                  </Typography.Title>
                </ColSize>
                <Table
                  bordered
                  className='person-table'
                  columns={MOCK_TABLE_COLUMNS_HEADER}
                  dataSource={coverages}
                  pagination={false}
                  scroll={{ x: 'auto' }}
                />
              </React.Fragment>
            );
          });
        })}
      </ContentSection>

      <ObjectFormSection
        coveragesListInfo={coveragesListInfo[0]}
        numberComponent={theftInsuranceComponent.nroDeComponente}
      />
      <TransferRightsForm theftComponent={theftInsuranceComponent} />
    </CardAnt>
  );
};
